// Pricing component file

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { FaCheck, FaTimes, FaChevronLeft, FaChevronRight, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { loadStripe } from '@stripe/stripe-js';
import toast from 'react-hot-toast';
import { useMobileDetector } from '../utils/deviceDetection';
import axiosInstance from '../utils/axiosInstance';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Tier ordering for comparison
const TIER_ORDER = ['basic','pro','enterprise'];

const tiers = [
  {
    name: 'Basic',
    price: '49.99',
    interval: 'month',
    productId: 'prod_R2TeQ4r5iOH6CG',
    priceId: 'price_1RE0asE1a6rnB8cNkqBkhHi9',
    trialDays: 14,
    features: [
      { text: 'Up to 25 customers', included: true },
      { text: '150 SMS/Email notifications per month', included: true },
      { text: 'Customer Management', included: true },
      { text: 'Job Scheduling', included: true },
      { text: 'Route Planning', included: true },
      { text: 'Job Tracking', included: true },
      { text: 'Team Management', included: true },
      { text: 'Interactive Maps', included: true },
      { text: 'Business Analytics', included: true },
      { text: 'Invoicing & Payments', included: true },
      { text: 'Email support', included: true }
    ],
    recommended: false,
  },
  {
    name: 'Pro',
    price: '79.99',
    interval: 'month',
    productId: 'prod_R2TfmQYMHxix1e',
    priceId: 'price_1RE0asE1a6rnB8cNhmT34chW',
    features: [
      { text: 'Up to 100 customers', included: true },
      { text: '250 SMS/Email notifications per month', included: true },
      { text: 'Customer Management', included: true },
      { text: 'Job Scheduling', included: true },
      { text: 'Route Planning', included: true },
      { text: 'Job Tracking', included: true },
      { text: 'Team Management', included: true },
      { text: 'Interactive Maps', included: true },
      { text: 'Business Analytics', included: true },
      { text: 'Invoicing & Payments', included: true },
      { text: 'Priority email support', included: true }
    ],
    recommended: true,
  },
  {
    name: 'Enterprise',
    price: '199.99',
    interval: 'month',
    productId: 'prod_R2TgIYi0HUAYxf',
    priceId: 'price_1RE0asE1a6rnB8cNpTsMYNaV',
    features: [
      { text: 'Unlimited customers', included: true },
      { text: 'Unlimited SMS/Email notifications', included: true },
      { text: 'Customer Management', included: true },
      { text: 'Job Scheduling', included: true },
      { text: 'Route Planning', included: true },
      { text: 'Job Tracking', included: true },
      { text: 'Team Management', included: true },
      { text: 'Interactive Maps', included: true },
      { text: 'Business Analytics', included: true },
      { text: 'Invoicing & Payments', included: true },
      { text: 'Priority email & phone support', included: true },
      { text: 'Custom solutions', included: true }
    ],
    recommended: false,
  }
];

// Mobile-optimized pricing tier with collapsed feature list
const MobilePricingTier = ({ tier, onSelect, active }) => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  
  // Show only key features by default
  const keyFeatures = tier.features.slice(0, 3);
  const remainingFeatures = tier.features.slice(3);
  
  return (
    <div className={`bg-gradient-to-br from-surface to-gray-800 rounded-lg border border-gray-700 shadow-xl p-6 h-full flex flex-col ${
      active ? 'scale-100' : 'scale-95 opacity-50'
    } transition-all duration-300`}>
      {tier.recommended && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-3 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap">
            Recommended
          </span>
        </div>
      )}
      {tier.trialDays && (
        <div className="absolute -top-3 -right-3 bg-green-500 text-white px-3 py-0.5 rounded-full transform rotate-12 shadow-lg">
          <span className="text-xs font-bold whitespace-nowrap">
            {tier.trialDays} Days Free
          </span>
        </div>
      )}
      <div className="flex flex-col h-full">
        <div className="text-center mb-4">
          <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
          <div className="mb-4">
            <span className="text-3xl font-bold text-blue-400">${tier.price}</span>
            <span className="text-blue-200">/month</span>
          </div>
        </div>
        
        <ul className="flex-grow space-y-2 mb-4">
          {keyFeatures.map((feature, index) => (
            <li key={index} className="flex items-center text-blue-100 text-sm">
              <FaCheck className="text-green-400 mr-2 flex-shrink-0" />
              <span>{feature.text}</span>
            </li>
          ))}
          
          {showAllFeatures && remainingFeatures.map((feature, index) => (
            <li key={`remaining-${index}`} className="flex items-center text-blue-100 text-sm">
              <FaCheck className="text-green-400 mr-2 flex-shrink-0" />
              <span>{feature.text}</span>
            </li>
          ))}
        </ul>
        
        {remainingFeatures.length > 0 && (
          <button 
            onClick={() => setShowAllFeatures(!showAllFeatures)}
            className="text-blue-400 text-sm mb-4 hover:underline"
          >
            {showAllFeatures ? 'Show less' : `+${remainingFeatures.length} more features`}
          </button>
        )}
        
        {active && (
          <button
            onClick={() => onSelect(tier)}
            className="w-full mt-auto px-6 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium hover:from-blue-600 hover:to-blue-700 transform hover:scale-105 transition duration-200 shadow-lg hover:shadow-blue-500/50"
          >
            {tier.trialDays ? 'Start Free Trial' : 'Select Plan'}
          </button>
        )}
      </div>
    </div>
  );
};

// Simple mobile pricing item for All Plans view with expandable features
const SimpleMobilePricingItem = ({ tier, onSelect }) => {
  const [showFeatures, setShowFeatures] = useState(false);
  
  return (
    <div className="bg-gradient-to-br from-surface to-gray-800 rounded-lg border border-gray-700 shadow-lg mb-4">
      {/* Plan Header - Always visible */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-bold text-white">{tier.name}</h3>
          <div>
            <span className="text-xl font-bold text-blue-400">${tier.price}</span>
            <span className="text-blue-200">/mo</span>
          </div>
        </div>
        
        <div className="flex justify-between mb-2">
          <div className="text-blue-100 text-sm">
            {tier.features[0].text}
          </div>
          {tier.recommended && (
            <span className="bg-blue-500 text-white px-2 py-0.5 rounded-full text-xs whitespace-nowrap">
              Recommended
            </span>
          )}
        </div>
        
        <div className="flex justify-between items-center">
          <button 
            onClick={() => setShowFeatures(!showFeatures)}
            className="text-blue-400 text-sm flex items-center"
          >
            {showFeatures ? 'Hide features' : 'Show features'}
            {showFeatures ? <FaChevronUp className="ml-1" /> : <FaChevronDown className="ml-1" />}
          </button>
          
          {tier.trialDays && (
            <span className="bg-green-500 text-white px-2 py-0.5 rounded-full text-xs whitespace-nowrap">
              {tier.trialDays} Days Free
            </span>
          )}
        </div>
      </div>
      
      {/* Features - Collapsible */}
      {showFeatures && (
        <div className="p-4 border-b border-gray-700">
          <h4 className="text-sm font-medium text-blue-200 mb-3">Features:</h4>
          <ul className="space-y-2">
            {tier.features.map((feature, idx) => (
              <li key={idx} className="flex items-start text-sm">
                {feature.included ? (
                  <FaCheck className="text-green-400 mt-1 mr-2 flex-shrink-0" />
                ) : (
                  <FaTimes className="text-red-400 mt-1 mr-2 flex-shrink-0" />
                )}
                <span className="text-blue-100">{feature.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* CTA Button */}
      <div className="p-4">
        <button
          onClick={() => onSelect(tier)}
          className="w-full py-2.5 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium transition duration-200"
        >
          {tier.trialDays ? 'Start Free Trial' : 'Select Plan'}
        </button>
      </div>
    </div>
  );
};

// Desktop pricing tier (original)
const PricingTier = ({ tier, onSelect }) => (
  <div className={`relative bg-gradient-to-br from-surface to-gray-800 rounded-lg border border-gray-700 shadow-xl p-8 h-full flex flex-col ${
    tier.recommended ? 'transform scale-105' : ''
  }`}>
    {tier.recommended && (
      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
        <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
          Recommended
        </span>
      </div>
    )}
    {tier.trialDays && (
      <div className="absolute -top-4 -right-4 bg-green-500 text-white px-4 py-1 rounded-full transform rotate-12 shadow-lg">
        <span className="text-sm font-bold whitespace-nowrap">
          {tier.trialDays} Days Free
        </span>
      </div>
    )}
    <div className="flex flex-col h-full">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-white mb-4">{tier.name}</h3>
        <div className="mb-6">
          <span className="text-4xl font-bold text-blue-400">${tier.price}</span>
          <span className="text-blue-200">/month</span>
        </div>
      </div>
      
      <ul className="flex-grow space-y-4">
        {tier.features.map((feature, index) => (
          <li key={index} className={`flex items-center text-blue-100 ${
            index < 2 ? 'bg-blue-900/30 p-3 rounded-lg border border-blue-500/30' : ''
          }`}>
            <FaCheck className={`${index < 2 ? 'text-blue-400' : 'text-green-400'} mr-3 flex-shrink-0`} />
            <span className={`${index < 2 ? 'text-blue-200 font-medium' : ''}`}>{feature.text}</span>
          </li>
        ))}
      </ul>
      
      <button
        onClick={() => onSelect(tier)}
        className="w-full mt-8 px-6 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium hover:from-blue-600 hover:to-blue-700 transform hover:scale-105 transition duration-200 shadow-lg hover:shadow-blue-500/50"
      >
        {tier.trialDays ? 'Start Free Trial' : 'Select Plan'}
      </button>
    </div>
  </div>
);

// New simplified Mobile Pricing view showing all plans at once
const MobileAllPlansView = ({ tiers, onSelect }) => {
  return (
    <div>
      {tiers.map((tier, index) => (
        <SimpleMobilePricingItem 
          key={index}
          tier={tier}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
};

// Mobile Pricing Carousel component with improved swiping
const MobilePricingCarousel = ({ tiers, onSelect }) => {
  // We're removing the carousel functionality since we'll always show all plans
  return (
    <div className="mb-6">
      <h2 className="text-xl font-semibold text-white mb-4">Choose Your Plan</h2>
      <MobileAllPlansView tiers={tiers} onSelect={onSelect} />
    </div>
  );
};

const Pricing = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const isMobile = useMobileDetector();

  useEffect(() => {
    if (location.state?.message) {
      toast(location.state.message);
    }
  }, [location]);

  // Function to create a checkout session
  const createCheckoutSession = async (priceId) => {
    const response = await axiosInstance.post('/payment/create-checkout-session', { priceId });
    return response.data.sessionId;
  };

  const handleTierSelect = async (tier) => {
    try {
      if (!user) {
        sessionStorage.setItem('selectedPlan', JSON.stringify(tier));
        navigate('/register', {
          state: {
            message: `Please register to continue with ${tier.name} plan`,
            returnPath: '/pricing'
          }
        });
        return;
      }

      const current = user.subscriptionTier;
      const selected = tier.name.toLowerCase();

      // If user selects the same tier
      if (current === selected) {
        // If subscription is cancelled, resume it
        if (user.subscriptionCancelledAt) {
          const resp = await axiosInstance.post('/subscription/resume');
          await refreshUser();
          setAlert({
            type: 'success',
            message: `Your subscription has been resumed. Next billing on ${new Date(resp.data.subscription.current_period_end * 1000).toLocaleDateString()}.`
          });
        } else {
          setAlert({ type: 'info', message: 'You are already on this plan.' });
        }
        return;
      }

      // Determine downgrade vs upgrade
      if (current && TIER_ORDER.indexOf(selected) < TIER_ORDER.indexOf(current)) {
        // Downgrade: schedule at period end
        const resp = await axiosInstance.post('/subscription/update', { newPriceId: tier.priceId });
        await refreshUser();
        setAlert({
          type: 'success',
          message: `Downgrade scheduled. Your plan will switch on ${new Date(resp.data.nextBillingDate).toLocaleDateString()}.`
        });
        return;
      }

      // Otherwise proceed with checkout session for upgrades or new subscriptions
      const stripe = await stripePromise;
      const sessionId = await createCheckoutSession(tier.priceId);
      const result = await stripe.redirectToCheckout({ sessionId });

      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (error) {
      console.error('Plan selection error:', error);
      if (error.message === 'Please sign in to continue') {
        navigate('/signin', {
          state: { 
            message: 'Please sign in to continue with plan selection',
            returnPath: '/pricing'
          }
        });
      } else {
        setAlert({
          type: 'error',
          message: error.message || 'Failed to process plan selection'
        });
      }
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-900 to-background text-text min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="text-center mb-12 md:mb-16 relative pt-4 md:pt-8">
          <div className="absolute inset-0 blur-[100px] bg-gradient-to-r from-blue-500/30 to-purple-500/30 transform rotate-12"></div>
          <h1 className="relative text-4xl md:text-5xl font-bold mb-4 md:mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto relative">
            Choose the perfect plan for your lawn care business
          </p>
        </div>

        {alert && (
          <div className={`p-4 rounded-md mb-8 ${
            alert.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
          }`}>
            {alert.message}
          </div>
        )}

        {isMobile ? (
          // Mobile: Enhanced carousel or all plans view
          <div className="mb-12">
            <MobilePricingCarousel tiers={tiers} onSelect={handleTierSelect} />
          </div>
        ) : (
          // Desktop: Grid layout with all plans
          <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
            {tiers.map((tier) => (
              <PricingTier key={tier.name} tier={tier} onSelect={handleTierSelect} />
            ))}
          </div>
        )}

        <div className="text-center">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4">
            Need something different?
          </h2>
          <p className="text-blue-100 mb-6 md:mb-8">
            Contact us for custom enterprise solutions tailored to your business needs
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="px-6 py-2.5 md:px-8 md:py-3 rounded-lg bg-white/10 backdrop-blur-sm text-white font-medium hover:bg-white/20 transform hover:scale-105 transition duration-200"
          >
            Contact Sales
          </button>
        </div>
      </main>
    </div>
  );
};

export default Pricing;

